import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Layout } from '../components/layout/Layout';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    CheckCircle, 
    Truck, 
    CreditCard,
    Building2,
    Package,
    AlertTriangle,
    Loader2,
    Gift,
    MapPin,
    Plus,
    Star,
    X,
    Landmark,
    Wallet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CheckoutPage = () => {
    const { token, user } = useAuth();
    const { cart, clearCart, hasItems, formattedTime, isExpiringSoon } = useCart();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [loadingQuotes, setLoadingQuotes] = useState(false);
    const [loadingAddresses, setLoadingAddresses] = useState(true);
    const [shippingMethod, setShippingMethod] = useState('');
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('stripe');
    const [notes, setNotes] = useState('');
    const [orderId, setOrderId] = useState(null);
    const [orderCompleted, setOrderCompleted] = useState(false); // Prevent redirect after order
    
    // Confirmation modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    
    // Shipping quotes
    const [shippingQuotes, setShippingQuotes] = useState([]);
    const [shippingInfo, setShippingInfo] = useState({
        free_shipping_threshold: 500,
        free_shipping_applied: false,
        remaining_for_free: 0
    });
    
    // User saved addresses
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState('');
    const [addressMode, setAddressMode] = useState('saved'); // 'saved' or 'manual'
    
    // Credit balance
    const [creditBalance, setCreditBalance] = useState(0);
    
    // Delivery address (for quotes)
    const [deliveryAddress, setDeliveryAddress] = useState({
        name: '',
        street: '',
        street2: '',
        postal_code: user?.postal_code || '',
        city: user?.city || '',
        country: 'FR',
        phone: ''
    });

    const countries = [
        { code: 'FR', name: 'France' },
        { code: 'BE', name: 'Belgique' },
        { code: 'DE', name: 'Allemagne' },
        { code: 'ES', name: 'Espagne' },
        { code: 'IT', name: 'Italie' },
        { code: 'NL', name: 'Pays-Bas' },
        { code: 'PT', name: 'Portugal' },
        { code: 'CH', name: 'Suisse' },
        { code: 'LU', name: 'Luxembourg' },
        { code: 'GB', name: 'Royaume-Uni' }
    ];

    // Redirect to cart if no items (but not if order just completed)
    useEffect(() => {
        if (!hasItems && !loading && !loadingAddresses && !orderCompleted) {
            navigate('/cart');
        }
    }, [hasItems, loading, loadingAddresses, orderCompleted, navigate]);

    // Check for Stripe return (canceled payment)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const canceled = urlParams.get('canceled');
        const orderId = urlParams.get('order_id');
        
        if (canceled === 'true' && orderId) {
            // User returned from Stripe without paying - cancel the order
            const cancelOrder = async () => {
                try {
                    await axios.post(
                        `${API_URL}/payments/cancel/${orderId}`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.info('Paiement annulé. Vos produits sont de retour dans votre panier.');
                } catch (error) {
                    console.error('Error canceling order:', error);
                }
                // Clean URL
                window.history.replaceState({}, '', window.location.pathname);
            };
            cancelOrder();
        }
    }, [token]);

    // Fetch user's saved addresses on mount
    useEffect(() => {
        fetchSavedAddresses();
        fetchCreditBalance();
    }, []);

    const fetchCreditBalance = async () => {
        try {
            const response = await axios.get(`${API_URL}/account/credit`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCreditBalance(response.data.total_credit || 0);
        } catch (error) {
            console.error('Error fetching credit:', error);
        }
    };

    const fetchSavedAddresses = async () => {
        try {
            const response = await axios.get(`${API_URL}/user/addresses`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const addresses = response.data.shipping_addresses || [];
            setSavedAddresses(addresses);
            
            // Auto-select default address if exists
            const defaultAddr = addresses.find(a => a.is_default);
            if (defaultAddr) {
                setSelectedAddressId(defaultAddr.id);
                setDeliveryAddress({
                    name: defaultAddr.name || '',
                    street: defaultAddr.street || '',
                    street2: defaultAddr.street2 || '',
                    postal_code: defaultAddr.postal_code || '',
                    city: defaultAddr.city || '',
                    country: defaultAddr.country || 'FR',
                    phone: defaultAddr.phone || ''
                });
                setAddressMode('saved');
            } else if (addresses.length > 0) {
                setSelectedAddressId(addresses[0].id);
                setDeliveryAddress({
                    name: addresses[0].name || '',
                    street: addresses[0].street || '',
                    street2: addresses[0].street2 || '',
                    postal_code: addresses[0].postal_code || '',
                    city: addresses[0].city || '',
                    country: addresses[0].country || 'FR',
                    phone: addresses[0].phone || ''
                });
                setAddressMode('saved');
            } else {
                setAddressMode('manual');
            }
        } catch (error) {
            console.error('Error fetching addresses:', error);
            setAddressMode('manual');
        } finally {
            setLoadingAddresses(false);
        }
    };

    const handleAddressSelect = (addressId) => {
        setSelectedAddressId(addressId);
        const address = savedAddresses.find(a => a.id === addressId);
        if (address) {
            setDeliveryAddress({
                name: address.name || '',
                street: address.street || '',
                street2: address.street2 || '',
                postal_code: address.postal_code || '',
                city: address.city || '',
                country: address.country || 'FR',
                phone: address.phone || ''
            });
        }
    };

    // Fetch shipping quotes when address changes
    useEffect(() => {
        if (deliveryAddress.postal_code && deliveryAddress.city) {
            fetchShippingQuotes();
        }
    }, [deliveryAddress.postal_code, deliveryAddress.city]);

    const fetchShippingQuotes = async () => {
        if (!deliveryAddress.postal_code || !deliveryAddress.city) return;
        
        setLoadingQuotes(true);
        try {
            const response = await axios.post(
                `${API_URL}/shipping/quote`,
                deliveryAddress,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Filter out relay services (not fully implemented yet) and sort: Classic/Domicile first
            let quotes = (response.data.quotes || []).filter(q => {
                // Exclude relay services until API integration is complete
                const isRelay = q.is_relay || 
                    q.carrier_code?.toLowerCase().includes('relais') || 
                    q.carrier_name?.toLowerCase().includes('relais') ||
                    q.service?.toLowerCase().includes('relais') ||
                    q.service?.toLowerCase().includes('pickup');
                return !isRelay;
            });
            
            // Sort by price ascending
            quotes.sort((a, b) => (a.price || 0) - (b.price || 0));
            
            setShippingQuotes(quotes);
            setShippingInfo({
                free_shipping_threshold: response.data.free_shipping_threshold,
                free_shipping_applied: response.data.free_shipping_applied,
                remaining_for_free: response.data.remaining_for_free
            });
            
            // Auto-select first option (cheapest, which should be Classic)
            if (quotes.length > 0 && !shippingMethod) {
                setShippingMethod(quotes[0].carrier_code);
                setSelectedCarrier(quotes[0]);
            }
        } catch (error) {
            console.error('Error fetching quotes:', error);
            // Use fallback options (no pickup for B2B)
            setShippingQuotes([
                { carrier_code: 'STANDARD', carrier_name: 'Livraison standard', price: 15, delay: '3-5 jours' },
                { carrier_code: 'EXPRESS', carrier_name: 'Livraison express', price: 35, delay: '24-48h' }
            ]);
        } finally {
            setLoadingQuotes(false);
        }
    };

    // Handle shipping option selection
    const selectShippingOption = (quote) => {
        setShippingMethod(quote.carrier_code);
        setSelectedCarrier(quote);
    };

    // Custom success toast for order confirmation
    const showOrderSuccessToast = (orderId) => {
        toast.success(
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                    <p className="font-semibold text-slate-900">Merci pour votre commande !</p>
                    <p className="text-sm text-slate-600">
                        Commande #{orderId?.slice(0, 8).toUpperCase()} enregistrée
                    </p>
                </div>
            </div>,
            {
                duration: 5000,
                style: {
                    padding: '16px',
                    borderRadius: '12px',
                    background: '#fff',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    border: '1px solid #e2e8f0'
                }
            }
        );
    };

    // Open confirmation modal instead of direct submit
    const handleConfirmClick = (e) => {
        e.preventDefault();
        
        if (!hasItems) {
            toast.error('Votre panier est vide');
            navigate('/catalog');
            return;
        }

        // Validate delivery address
        if (!deliveryAddress.postal_code || !deliveryAddress.city || !deliveryAddress.street) {
            toast.error('Veuillez remplir l\'adresse de livraison complète');
            return;
        }

        if (!shippingMethod) {
            toast.error('Veuillez choisir un mode de livraison');
            return;
        }

        // Show confirmation modal
        setShowConfirmModal(true);
    };

    const handleSubmit = async () => {
        setShowConfirmModal(false);
        setLoading(true);

        try {
            // Store cart data before clearing
            const orderItems = [...cart.items];
            const orderTotalHt = cart.total_ht;
            const orderTotalVat = cart.total_vat;
            const orderTotalTtc = cart.total_ttc;
            
            // Create order with delivery address
            const orderResponse = await axios.post(
                `${API_URL}/orders`,
                {
                    shipping_method: shippingMethod,
                    shipping_cost: selectedCarrier?.price || 0,
                    payment_method: paymentMethod,
                    notes: notes || null,
                    delivery_address: {
                        name: deliveryAddress.name,
                        street: deliveryAddress.street,
                        street2: deliveryAddress.street2,
                        postal_code: deliveryAddress.postal_code,
                        city: deliveryAddress.city,
                        country: deliveryAddress.country,
                        phone: deliveryAddress.phone
                    }
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const newOrderId = orderResponse.data.order.id;
            setOrderId(newOrderId);

            // If Stripe payment, redirect to Stripe checkout
            if (paymentMethod === 'stripe') {
                try {
                    const checkoutResponse = await axios.post(
                        `${API_URL}/payments/create-checkout`,
                        {
                            order_id: newOrderId,
                            origin_url: window.location.origin
                        },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    // Check if fully paid with credit
                    if (checkoutResponse.data.paid_with_credit) {
                        // Order was fully paid with credit, redirect to success page
                        toast.success('Commande payée avec votre crédit !');
                        setOrderCompleted(true);
                        clearCart();
                        navigate(`/order-success?order_id=${newOrderId}&method=credit`);
                        return;
                    }

                    // Redirect to Stripe checkout
                    window.location.href = checkoutResponse.data.checkout_url;
                    return; // Don't clear cart or show success - Stripe will redirect back
                } catch (stripeError) {
                    console.error('Stripe error:', stripeError);
                    toast.error('Erreur lors de la création du paiement Stripe');
                    // Order was created, but Stripe failed - redirect to success page anyway
                    setOrderCompleted(true);
                    clearCart();
                    navigate(`/order-success?order_id=${newOrderId}&method=bank_transfer`);
                    return;
                }
            }

            // For bank transfer or credit, redirect to order success page
            setOrderCompleted(true);
            clearCart();
            navigate(`/order-success?order_id=${newOrderId}&method=${paymentMethod}`);
        } catch (error) {
            const message = error.response?.data?.detail || 'Erreur lors de la commande';
            toast.error(message);
            
            // If products unavailable, redirect to cart
            if (error.response?.status === 400) {
                navigate('/cart');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!hasItems) {
        return (
            <Layout>
                <div className="container mx-auto px-4 py-20 text-center">
                    <p className="text-slate-500">Chargement...</p>
                </div>
            </Layout>
        );
    }

    const shippingCost = selectedCarrier?.price || 0;
    const shippingVat = shippingCost * 0.20; // TVA 20% sur la livraison
    const totalVat = cart.total_vat + shippingVat; // TVA produits + TVA livraison
    const totalWithShipping = cart.total_ttc + shippingCost + shippingVat;

    return (
        <Layout>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
                <Button 
                    variant="ghost" 
                    className="mb-6" 
                    onClick={() => navigate('/cart')}
                    data-testid="back-to-cart"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour au panier
                </Button>

                <h1 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                    Finaliser la commande
                </h1>

                {/* Timer Warning */}
                {isExpiringSoon && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 timer-warning" />
                        <div>
                            <p className="font-medium text-red-800">
                                Réservation expire dans <span className="font-mono">{formattedTime}</span>
                            </p>
                            <p className="text-sm text-red-600">Finalisez votre commande rapidement</p>
                        </div>
                    </div>
                )}

                <form onSubmit={handleConfirmClick}>
                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Left: Forms */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Billing Info */}
                            <Card className="border-slate-200">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Building2 className="w-5 h-5" />
                                        Informations de facturation
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <p className="text-slate-500">Entreprise</p>
                                            <p className="font-medium">{user?.company_name}</p>
                                        </div>
                                        <div>
                                            <p className="text-slate-500">N° TVA</p>
                                            <p className="font-medium font-mono">{user?.vat_number}</p>
                                        </div>
                                        <div>
                                            <p className="text-slate-500">Contact</p>
                                            <p className="font-medium">{user?.contact_name}</p>
                                        </div>
                                        <div>
                                            <p className="text-slate-500">Email</p>
                                            <p className="font-medium">{user?.email}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Shipping Address & Quotes */}
                            <Card className="border-slate-200">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Truck className="w-5 h-5" />
                                        Mode de livraison
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Franco de port info */}
                                    {shippingInfo.free_shipping_applied ? (
                                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
                                            <Gift className="w-5 h-5 text-emerald-600" />
                                            <span className="text-sm text-emerald-700 font-medium">
                                                Franco de port appliqué ! Livraison gratuite à partir de {shippingInfo.free_shipping_threshold}€ HT
                                            </span>
                                        </div>
                                    ) : shippingInfo.remaining_for_free > 0 && (
                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                                            <Gift className="w-5 h-5 text-amber-600" />
                                            <span className="text-sm text-amber-700">
                                                Plus que <strong>{shippingInfo.remaining_for_free.toFixed(2)}€ HT</strong> pour bénéficier de la livraison gratuite
                                            </span>
                                        </div>
                                    )}

                                    {/* Delivery address selection */}
                                    <div className="p-4 bg-slate-50 rounded-lg">
                                        <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                                            <MapPin className="w-4 h-4" />
                                            Adresse de livraison
                                        </p>
                                        
                                        {loadingAddresses ? (
                                            <div className="flex items-center justify-center py-4">
                                                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                                            </div>
                                        ) : savedAddresses.length > 0 ? (
                                            <div className="space-y-3">
                                                {/* Address selector */}
                                                <div className="space-y-2">
                                                    <Label className="text-xs text-slate-500">Choisir une adresse enregistrée</Label>
                                                    <Select
                                                        value={addressMode === 'saved' ? selectedAddressId : 'manual'}
                                                        onValueChange={(value) => {
                                                            if (value === 'manual') {
                                                                setAddressMode('manual');
                                                                setSelectedAddressId('');
                                                                setDeliveryAddress({
                                                                    name: '',
                                                                    street: '',
                                                                    street2: '',
                                                                    postal_code: '',
                                                                    city: '',
                                                                    country: 'FR',
                                                                    phone: ''
                                                                });
                                                            } else {
                                                                setAddressMode('saved');
                                                                handleAddressSelect(value);
                                                            }
                                                        }}
                                                    >
                                                        <SelectTrigger data-testid="address-selector">
                                                            <SelectValue placeholder="Sélectionner une adresse" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {savedAddresses.map((addr) => (
                                                                <SelectItem key={addr.id} value={addr.id}>
                                                                    <div className="flex items-center gap-2">
                                                                        {addr.is_default && <Star className="w-3 h-3 text-amber-500" />}
                                                                        <span>{addr.name || `${addr.street}, ${addr.city}`}</span>
                                                                    </div>
                                                                </SelectItem>
                                                            ))}
                                                            <SelectItem value="manual">
                                                                <div className="flex items-center gap-2">
                                                                    <Plus className="w-3 h-3" />
                                                                    <span>Saisir une autre adresse</span>
                                                                </div>
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>

                                                {/* Show selected address details */}
                                                {addressMode === 'saved' && selectedAddressId && (
                                                    <div className="p-3 bg-white border border-slate-200 rounded-lg text-sm">
                                                        {savedAddresses.find(a => a.id === selectedAddressId) && (
                                                            <>
                                                                {savedAddresses.find(a => a.id === selectedAddressId).name && (
                                                                    <p className="font-medium text-slate-900">
                                                                        {savedAddresses.find(a => a.id === selectedAddressId).name}
                                                                    </p>
                                                                )}
                                                                <p className="text-slate-600">{savedAddresses.find(a => a.id === selectedAddressId).street}</p>
                                                                {savedAddresses.find(a => a.id === selectedAddressId).street2 && (
                                                                    <p className="text-slate-600">{savedAddresses.find(a => a.id === selectedAddressId).street2}</p>
                                                                )}
                                                                <p className="text-slate-600">
                                                                    {savedAddresses.find(a => a.id === selectedAddressId).postal_code}{' '}
                                                                    {savedAddresses.find(a => a.id === selectedAddressId).city}
                                                                </p>
                                                                <p className="text-slate-500">
                                                                    {countries.find(c => c.code === savedAddresses.find(a => a.id === selectedAddressId).country)?.name}
                                                                </p>
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Manual address form */}
                                                {addressMode === 'manual' && (
                                                    <div className="space-y-3 pt-2">
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <div>
                                                                <Label htmlFor="postal_code" className="text-xs text-slate-500">Code postal *</Label>
                                                                <Input
                                                                    id="postal_code"
                                                                    value={deliveryAddress.postal_code}
                                                                    onChange={(e) => setDeliveryAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                                                                    placeholder="75001"
                                                                    className="mt-1"
                                                                    data-testid="manual-postal-code"
                                                                />
                                                            </div>
                                                            <div>
                                                                <Label htmlFor="city" className="text-xs text-slate-500">Ville *</Label>
                                                                <Input
                                                                    id="city"
                                                                    value={deliveryAddress.city}
                                                                    onChange={(e) => setDeliveryAddress(prev => ({ ...prev, city: e.target.value }))}
                                                                    placeholder="Paris"
                                                                    className="mt-1"
                                                                    data-testid="manual-city"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <Label htmlFor="street" className="text-xs text-slate-500">Adresse *</Label>
                                                            <Input
                                                                id="street"
                                                                value={deliveryAddress.street}
                                                                onChange={(e) => setDeliveryAddress(prev => ({ ...prev, street: e.target.value }))}
                                                                placeholder="123 rue de la Livraison"
                                                                className="mt-1"
                                                                data-testid="manual-street"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label htmlFor="street2" className="text-xs text-slate-500">Complément</Label>
                                                            <Input
                                                                id="street2"
                                                                value={deliveryAddress.street2}
                                                                onChange={(e) => setDeliveryAddress(prev => ({ ...prev, street2: e.target.value }))}
                                                                placeholder="Bâtiment, étage..."
                                                                className="mt-1"
                                                                data-testid="manual-street2"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label htmlFor="phone" className="text-xs text-slate-500">Téléphone</Label>
                                                            <Input
                                                                id="phone"
                                                                value={deliveryAddress.phone}
                                                                onChange={(e) => setDeliveryAddress(prev => ({ ...prev, phone: e.target.value }))}
                                                                placeholder="+33 6 00 00 00 00"
                                                                className="mt-1"
                                                                data-testid="manual-phone"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            /* No saved addresses - show manual form */
                                            <div className="space-y-3">
                                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2">
                                                    <MapPin className="w-4 h-4" />
                                                    <span>Aucune adresse enregistrée. <a href="/addresses" className="underline font-medium">Gérer mes adresses</a></span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <Label htmlFor="postal_code" className="text-xs text-slate-500">Code postal *</Label>
                                                        <Input
                                                            id="postal_code"
                                                            value={deliveryAddress.postal_code}
                                                            onChange={(e) => setDeliveryAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                                                            placeholder="75001"
                                                            className="mt-1"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label htmlFor="city" className="text-xs text-slate-500">Ville *</Label>
                                                        <Input
                                                            id="city"
                                                            value={deliveryAddress.city}
                                                            onChange={(e) => setDeliveryAddress(prev => ({ ...prev, city: e.target.value }))}
                                                            placeholder="Paris"
                                                            className="mt-1"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label htmlFor="street" className="text-xs text-slate-500">Adresse *</Label>
                                                    <Input
                                                        id="street"
                                                        value={deliveryAddress.street}
                                                        onChange={(e) => setDeliveryAddress(prev => ({ ...prev, street: e.target.value }))}
                                                        placeholder="123 rue de la Livraison"
                                                        className="mt-1"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="street2" className="text-xs text-slate-500">Complément</Label>
                                                    <Input
                                                        id="street2"
                                                        value={deliveryAddress.street2}
                                                        onChange={(e) => setDeliveryAddress(prev => ({ ...prev, street2: e.target.value }))}
                                                        placeholder="Bâtiment, étage..."
                                                        className="mt-1"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="phone" className="text-xs text-slate-500">Téléphone</Label>
                                                    <Input
                                                        id="phone"
                                                        value={deliveryAddress.phone}
                                                        onChange={(e) => setDeliveryAddress(prev => ({ ...prev, phone: e.target.value }))}
                                                        placeholder="+33 6 00 00 00 00"
                                                        className="mt-1"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                    </div>

                                    {/* Shipping options - Grid layout like reference */}
                                    {loadingQuotes ? (
                                        <div className="flex items-center justify-center py-8">
                                            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                                            <span className="ml-2 text-slate-600">Calcul des tarifs...</span>
                                        </div>
                                    ) : shippingQuotes.length > 0 ? (
                                        <div>
                                            <h3 className="font-semibold text-slate-900 mb-4">Modes de livraison</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {shippingQuotes.map((quote) => (
                                                    <div
                                                        key={quote.carrier_code}
                                                        className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                                            shippingMethod === quote.carrier_code 
                                                                ? 'border-blue-500 bg-blue-50/50 shadow-sm' 
                                                                : 'border-slate-200 hover:border-slate-300 bg-white'
                                                        }`}
                                                        onClick={() => selectShippingOption(quote)}
                                                        data-testid={`shipping-${quote.carrier_code}`}
                                                    >
                                                        {/* Checkbox & Header */}
                                                        <div className="flex items-start justify-between mb-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                                                    shippingMethod === quote.carrier_code 
                                                                        ? 'border-blue-500 bg-blue-500' 
                                                                        : 'border-slate-300'
                                                                }`}>
                                                                    {shippingMethod === quote.carrier_code && (
                                                                        <CheckCircle className="w-4 h-4 text-white" />
                                                                    )}
                                                                </div>
                                                                {quote.logo_url ? (
                                                                    <img src={quote.logo_url} alt={quote.carrier_name} className="h-6 w-auto" />
                                                                ) : (
                                                                    <span className="font-semibold text-slate-900">{quote.carrier_name}</span>
                                                                )}
                                                                {quote.service && (
                                                                    <span className="text-sm text-slate-500">- {quote.service}</span>
                                                                )}
                                                            </div>
                                                            <div className="text-right">
                                                                {quote.is_free || quote.price === 0 ? (
                                                                    <span className="text-lg font-bold text-emerald-600">Gratuit</span>
                                                                ) : (
                                                                    <span className="text-lg font-bold text-blue-600">{quote.price.toFixed(2)}€</span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Details Grid */}
                                                        <div className="grid grid-cols-4 gap-2 text-sm">
                                                            <div>
                                                                <p className="text-slate-400 text-xs uppercase">Cut-off</p>
                                                                <p className="font-medium text-slate-700">{quote.cutoff || '17h'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400 text-xs uppercase">Livraison</p>
                                                                <p className="font-medium text-slate-700">{quote.delay}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400 text-xs uppercase flex items-center gap-1">
                                                                    Franco
                                                                    <span className="w-3 h-3 bg-blue-500 text-white rounded-full text-[8px] flex items-center justify-center">i</span>
                                                                </p>
                                                                <p className="font-medium text-slate-700">{quote.franco || 500}€</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-slate-400 text-xs uppercase">Samedi</p>
                                                                <p className="font-medium text-slate-700">{quote.saturday_delivery ? 'Oui' : 'Non'}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-500 text-center py-4">
                                            Entrez votre adresse pour voir les options de livraison
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Payment - Only show if there's remaining amount to pay */}
                            {creditBalance < totalWithShipping && (
                                <Card className="border-slate-200">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <CreditCard className="w-5 h-5" />
                                            Mode de paiement
                                            {creditBalance > 0 && (
                                                <Badge className="ml-2 bg-green-100 text-green-700 text-xs">
                                                    Reste: {(totalWithShipping - creditBalance).toFixed(2)} €
                                                </Badge>
                                            )}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                                            <div
                                                className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                                                    paymentMethod === 'stripe' 
                                                        ? 'border-blue-500 bg-blue-50' 
                                                        : 'border-slate-200 hover:border-slate-300'
                                                }`}
                                                data-testid="payment-stripe"
                                            >
                                                <label htmlFor="stripe" className="flex items-center gap-3 cursor-pointer flex-1">
                                                    <RadioGroupItem value="stripe" id="stripe" />
                                                    <div>
                                                        <span className="font-medium block">
                                                            Carte bancaire (Stripe)
                                                        </span>
                                                        <span className="text-sm text-slate-500 block">
                                                            Paiement sécurisé par carte
                                                        </span>
                                                    </div>
                                                </label>
                                                <div className="flex items-center gap-1">
                                                    <svg className="h-6" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg"><path fill="#635BFF" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.02 1.04-.06 1.48zm-6.3-5.63c-1.03 0-1.99.73-2.1 2.46h4.16c-.03-1.8-.97-2.46-2.06-2.46zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-5.13L32.37 0v3.77l-4.13.88V.44zm-4.32 9.35v10.22H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.45-3.32.43zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.41zm-7.23 1.83c0-.62-.22-1.07-.63-1.34-.4-.26-1.02-.4-1.84-.4-.65 0-1.18.1-1.6.3-.41.2-.62.48-.62.84 0 .32.16.57.48.75.31.18.77.28 1.36.3l.48.01c.62.01 1.13.1 1.53.25.4.16.68.36.85.6.17.25.25.55.25.9 0 .43-.1.83-.32 1.2-.22.36-.58.65-1.07.87-.5.21-1.15.32-1.98.32-1.06 0-1.92-.24-2.57-.72a2.4 2.4 0 0 1-.98-2h1.3c.02.5.22.88.6 1.12.37.25.88.37 1.52.37.58 0 1.03-.1 1.35-.3.32-.2.48-.48.48-.84 0-.3-.12-.53-.38-.7-.25-.16-.64-.28-1.17-.35l-.7-.09c-.87-.1-1.52-.32-1.96-.65-.44-.33-.66-.8-.66-1.4 0-.47.12-.87.37-1.22.24-.35.59-.62 1.03-.81.45-.19.97-.28 1.57-.28.95 0 1.71.22 2.27.66.56.44.86 1.04.89 1.8h-1.35v.01zM.96 9.1l2.6 11.03h1.56L7.42 9.1h1.35l-2.72 11.73c-.2.88-.52 1.5-.94 1.86-.42.36-.97.54-1.66.54-.18 0-.34 0-.48-.02a2.2 2.2 0 0 1-.4-.08v-1.17c.1.03.2.05.32.06.12.02.23.02.33.02.34 0 .6-.1.8-.3.18-.2.33-.51.43-.93l.2-.8L.96 9.1h1.35-.35z"/></svg>
                                                </div>
                                            </div>

                                            <div
                                                className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors mt-3 ${
                                                    paymentMethod === 'bank_transfer' 
                                                        ? 'border-blue-500 bg-blue-50' 
                                                        : 'border-slate-200 hover:border-slate-300'
                                                }`}
                                                data-testid="payment-bank"
                                            >
                                                <label htmlFor="bank_transfer" className="flex items-center gap-3 cursor-pointer flex-1">
                                                    <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                                                    <div>
                                                        <span className="font-medium block">
                                                            Virement bancaire
                                                        </span>
                                                        <span className="text-sm text-slate-500 block">
                                                            Paiement par virement SEPA
                                                        </span>
                                                    </div>
                                                </label>
                                            </div>
                                        </RadioGroup>

                                        {paymentMethod === 'bank_transfer' && (
                                            <div className="mt-4 p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
                                                <p className="font-medium text-slate-900 mb-2">Instructions de paiement</p>
                                                <p>Les coordonnées bancaires vous seront communiquées après validation de la commande.</p>
                                            </div>
                                        )}

                                        {paymentMethod === 'stripe' && (
                                            <div className="mt-4 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
                                                <p className="font-medium text-blue-900 mb-2">Paiement sécurisé</p>
                                                <p>Vous serez redirigé vers la page de paiement Stripe après confirmation.</p>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Payment by Credit - Full coverage */}
                            {creditBalance >= totalWithShipping && (
                                <Card className="border-green-200 bg-green-50">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2 text-green-700">
                                            <Wallet className="w-5 h-5" />
                                            Paiement par crédit
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-center py-4">
                                            <div className="text-3xl font-bold text-green-600 mb-2">
                                                {totalWithShipping.toFixed(2)} €
                                            </div>
                                            <p className="text-green-700">
                                                Sera prélevé sur votre crédit disponible de {creditBalance.toFixed(2)} €
                                            </p>
                                            <p className="text-sm text-green-600 mt-2">
                                                Solde restant après commande: {(creditBalance - totalWithShipping).toFixed(2)} €
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Notes */}
                            <Card className="border-slate-200">
                                <CardHeader>
                                    <CardTitle>Notes (optionnel)</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Textarea
                                        placeholder="Instructions spéciales, remarques..."
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        rows={3}
                                        data-testid="order-notes"
                                    />
                                </CardContent>
                            </Card>
                        </div>

                        {/* Right: Summary */}
                        <div>
                            <Card className="border-slate-200 sticky top-20">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Package className="w-5 h-5" />
                                        Récapitulatif
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {/* Items summary */}
                                    <div className="max-h-48 overflow-auto space-y-2">
                                        {cart.items.map((item) => (
                                            <div key={item.id} className="flex justify-between text-sm">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium truncate">{item.brand} {item.model}</p>
                                                    <p className="text-xs text-slate-500">{item.storage_capacity}GB - {item.color}</p>
                                                </div>
                                                <span className="font-medium ml-2">{item.price.toFixed(2)} €</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="border-t border-slate-200 pt-4 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-600">Sous-total HT ({cart.items.length} articles)</span>
                                            <span>{cart.total_ht.toFixed(2)} €</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-600">TVA produits</span>
                                            <span>{cart.total_vat.toFixed(2)} €</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-600">
                                                Livraison {selectedCarrier?.carrier_name && `(${selectedCarrier.carrier_name})`}
                                            </span>
                                            <span className={shippingCost === 0 ? 'text-emerald-600 font-medium' : ''}>
                                                {shippingCost === 0 ? 'Gratuit' : `${shippingCost.toFixed(2)} €`}
                                            </span>
                                        </div>
                                        {shippingCost > 0 && (
                                            <div className="flex justify-between text-sm">
                                                <span className="text-slate-600">TVA livraison (20%)</span>
                                                <span>{shippingVat.toFixed(2)} €</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="border-t border-slate-200 pt-4 space-y-2">
                                        <div className="flex justify-between text-lg font-bold">
                                            <span>Total TTC</span>
                                            <span>{totalWithShipping.toFixed(2)} €</span>
                                        </div>
                                        
                                        {/* Credit Applied */}
                                        {creditBalance > 0 && (
                                            <div className="flex justify-between text-sm text-green-600">
                                                <span className="flex items-center gap-1">
                                                    <Wallet className="w-4 h-4" />
                                                    Crédit disponible
                                                </span>
                                                <span className="font-semibold">-{Math.min(creditBalance, totalWithShipping).toFixed(2)} €</span>
                                            </div>
                                        )}
                                        
                                        {/* Final Amount to Pay */}
                                        {creditBalance > 0 && (
                                            <div className="flex justify-between text-lg font-bold pt-2 border-t border-dashed">
                                                <span>{creditBalance >= totalWithShipping ? 'Reste à payer' : 'À régler'}</span>
                                                <span className={creditBalance >= totalWithShipping ? 'text-green-600' : ''}>
                                                    {Math.max(0, totalWithShipping - creditBalance).toFixed(2)} €
                                                </span>
                                            </div>
                                        )}
                                        
                                        {/* Fully covered by credit message */}
                                        {creditBalance >= totalWithShipping && (
                                            <p className="text-center text-sm text-green-600 font-medium bg-green-50 rounded-lg py-2">
                                                ✓ Payé intégralement avec votre crédit
                                            </p>
                                        )}
                                    </div>

                                    {/* VAT breakdown */}
                                    <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="vat-margin text-xs">Marge</Badge>
                                            <span className="text-slate-600">
                                                {cart.items.filter(i => i.vat_regime === 'margin').length} produit(s)
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="vat-standard text-xs">TVA 20%</Badge>
                                            <span className="text-slate-600">
                                                {cart.items.filter(i => i.vat_regime === 'standard').length} produit(s)
                                            </span>
                                        </div>
                                    </div>

                                    <Button 
                                        type="submit" 
                                        className="w-full" 
                                        size="lg"
                                        disabled={loading}
                                        data-testid="submit-order"
                                    >
                                        {loading ? 'Validation...' : 'Passer commande'}
                                    </Button>

                                    <p className="text-xs text-center text-slate-500">
                                        En confirmant, vous acceptez nos conditions générales de vente.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </form>

                {/* Confirmation Modal */}
                {showConfirmModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200 relative">
                            {/* Icon - Different based on payment method */}
                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg ${
                                paymentMethod === 'stripe' 
                                    ? 'bg-gradient-to-br from-amber-400 to-orange-500' 
                                    : 'bg-gradient-to-br from-blue-500 to-blue-700'
                            }`}>
                                {paymentMethod === 'stripe' ? (
                                    <CreditCard className="w-8 h-8 text-white" />
                                ) : (
                                    <Landmark className="w-8 h-8 text-white" />
                                )}
                            </div>

                            {/* Title */}
                            <h2 className="text-2xl font-bold text-center text-slate-900 mb-2">
                                Confirmation
                            </h2>
                            
                            {/* Description - Payment method based on credit coverage */}
                            <p className="text-center text-slate-600 mb-6">
                                Vous avez choisi de valider votre commande par{' '}
                                <span className="font-semibold text-blue-600">
                                    {creditBalance >= totalWithShipping 
                                        ? 'Solde crédit'
                                        : creditBalance > 0
                                            ? `${paymentMethod === 'stripe' ? 'Carte Bancaire' : 'Virement Bancaire'} + Solde crédit`
                                            : paymentMethod === 'stripe' ? 'Carte Bancaire' : 'Virement Bancaire'
                                    }
                                </span>
                            </p>

                            {/* Order Summary */}
                            <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-slate-600">{cart.items.length} article(s)</span>
                                    <span className="font-medium">{cart.total_ht.toFixed(2)} € HT</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600">TVA</span>
                                    <span className="font-medium">{totalVat.toFixed(2)} €</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-600">Livraison</span>
                                    <span className="font-medium">
                                        {shippingCost === 0 ? 'Gratuit' : `${shippingCost.toFixed(2)} €`}
                                    </span>
                                </div>
                                {shippingCost > 0 && (
                                    <div className="flex justify-between text-slate-500">
                                        <span className="text-xs">dont TVA livraison (20%)</span>
                                        <span className="text-xs">{shippingVat.toFixed(2)} €</span>
                                    </div>
                                )}
                                
                                {/* Credit available */}
                                {creditBalance > 0 && (
                                    <div className="flex justify-between text-green-600 bg-green-50 -mx-4 px-4 py-2">
                                        <span className="flex items-center gap-1">
                                            <Gift className="w-4 h-4" />
                                            Crédit disponible
                                        </span>
                                        <span className="font-medium">-{Math.min(creditBalance, totalWithShipping).toFixed(2)} €</span>
                                    </div>
                                )}
                                
                                <div className="border-t border-slate-200 pt-2 flex justify-between">
                                    <span className="font-semibold text-slate-900">Total TTC</span>
                                    <span className="font-bold text-blue-600 text-lg">
                                        {creditBalance > 0 
                                            ? Math.max(0, totalWithShipping - creditBalance).toFixed(2)
                                            : totalWithShipping.toFixed(2)
                                        } €
                                    </span>
                                </div>
                                {creditBalance > 0 && creditBalance >= totalWithShipping && (
                                    <p className="text-sm text-green-600 text-center">
                                        Payé intégralement avec votre crédit
                                    </p>
                                )}
                            </div>

                            {/* Buttons */}
                            <div className="space-y-3">
                                <Button 
                                    className="w-full" 
                                    size="lg"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    data-testid="confirm-order-modal"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Validation en cours...
                                        </>
                                    ) : (
                                        'Je confirme'
                                    )}
                                </Button>
                                
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmModal(false)}
                                    className="w-full text-center text-sm text-slate-500 hover:text-blue-600 transition-colors py-2"
                                    disabled={loading}
                                >
                                    Modifier le mode de paiement
                                </button>
                            </div>

                            {/* Close button */}
                            <button
                                type="button"
                                onClick={() => setShowConfirmModal(false)}
                                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
                                disabled={loading}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </Layout>
    );
};

export default CheckoutPage;
